import { FC, useMemo } from 'react';
import { beautifyEnum, formatMoney } from 'src/utils/utility';
import { useDoubleChargeDetails } from 'src/use-cases/invoice/useDoubleChargeDetails';
import { useShipmentSelector } from '../../ShipmentSelector';
import { IDeviationInfoProps, ITableDeviationDetailsDTO, ITableInvoiceChargeDTO } from './types';
import { TableInvoiceCharge } from './TableInvoiceCharge';
import { TableDeviationDetails } from './TableDeviationDetails';
import { TableRateCardCharge } from './TableRateCardCharge';

export const DeviationInfo: FC<IDeviationInfoProps> = (props) => {
  const { deviation } = props;
  const { shipment, shipmentInvoiceId } = useShipmentSelector();

  const isDoubleCharge = deviation?.type === 'DOUBLE_CHARGE';
  const { data: doubleChargeDetail } = useDoubleChargeDetails(isDoubleCharge ? deviation?.id : '');

  const invoiceCharges: ITableInvoiceChargeDTO[] = useMemo(() => {
    if (isDoubleCharge && doubleChargeDetail?.invoiceLines?.length > 0) {
      return doubleChargeDetail.invoiceLines.map((line) => ({
        chargeName: line.chargeName,
        deviationType: line.deviationType,
        unitPrice: formatMoney(line.unitPrice, line.currency),
        quantity: line.quantity,
        invoiceNumber: line.invoiceNumber,
        deviationStatus: line.invoiceStatus,
        currentDeviationWorkflow: line.currentDeviationWorkflow,
        deviationId: line.deviationId,
        editable: line.editable,
        isCurrentInvoice: line.shipmentInvoiceId === shipmentInvoiceId,
      }));
    }
    const matchingCharge = shipment?.invoiceCharges?.find((c) => c.id === deviation?.chargeId);
    return [
      {
        chargeName: deviation?.chargeDescription,
        deviationType: deviation?.type,
        unitPrice: formatMoney(deviation?.invoiceUnitPrice, deviation?.deviationCurrency),
        quantity: deviation?.invoiceQuantity,
        discountAmount: matchingCharge?.discountAmount != null ? formatMoney(matchingCharge.discountAmount, matchingCharge.originalCurrency) : null,
      },
    ];
  }, [isDoubleCharge, doubleChargeDetail, deviation, shipment?.invoiceCharges, shipmentInvoiceId]);

  const invoiceDetails: ITableDeviationDetailsDTO[] = useMemo(
    () => [
      {
        category: beautifyEnum(deviation?.subType) || (deviation?.type === 'NEW_CHARGE' ? 'Unknown Charge' : beautifyEnum(deviation?.type)),
        totalAmount: formatMoney(deviation?.deviationAmountAfterTax, deviation?.deviationCurrency),
        amountPerUnit: formatMoney(deviation?.deviationAmountPerUnit, deviation?.deviationCurrency),
        excessQuantity: deviation?.deviationQuantity ?? 0,
      },
    ],
    [deviation]
  );

  return (
    <div className='px-24 flex flex-col gap-36'>
      <div className='relative'>
        <h2 className='mb-16 text-14 xl:text-16 font-bold text-primary uppercase'>Invoice Charge</h2>
        <TableInvoiceCharge data={invoiceCharges} />
      </div>
      {deviation?.matchingRateCards?.length > 0 && (
        <div className='relative'>
          <h2 className='mb-16 text-14 xl:text-16 font-bold text-primary uppercase'>MATCHING RATE CARD CHARGE</h2>
          <TableRateCardCharge data={deviation?.matchingRateCards} type={deviation?.matchingRateCards[0]?.chargeType} />
          <div className='mt-16 py-10 px-12 rounded-8 bg-neutral-10 border border-neutral-20 text-14 flex items-center gap-12 text-blue_grey-30'>
            Accessorial Charge:
            <strong className='text-neutral-80'>{deviation?.accessorial ? 'Yes' : 'No'}</strong>
          </div>
        </div>
      )}
      <div className='relative'>
        <h2 className='mb-16 text-14 xl:text-16 font-bold text-primary uppercase'>DEVIATION DETAILS</h2>
        <TableDeviationDetails data={invoiceDetails} type={deviation?.type} />
      </div>
    </div>
  );
};
